import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Smartphone, Loader2, RefreshCw, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import SignInBackground from '../components/SignInBackground';
import { BrandLogo } from '../components/BrandLogo';
import { useCMS } from '../context/CMSContext';
import { useAuth } from '../context/AuthContext';
import api from '../api';

const CODE_LEN = 6;
const RESEND_COOLDOWN = 30;
// Stable, non-numeric keys for each OTP slot. The 6 boxes have a fixed
// schema and never reorder, so positional identity is the correct semantic.
const DIGIT_SLOT_IDS = ['otp-a', 'otp-b', 'otp-c', 'otp-d', 'otp-e', 'otp-f'];

// Pure key handlers — extracted so the component stays simple.
const buildKeyDownHandler = ({ digits, inputsRef, setDigit, submit }) => (i, e) => {
  const actions = {
    Backspace: () => {
      if (digits[i] || i === 0) return;
      inputsRef.current[i - 1]?.focus();
      setDigit(i - 1, '');
    },
    ArrowLeft: () => { if (i > 0) inputsRef.current[i - 1]?.focus(); },
    ArrowRight: () => { if (i < CODE_LEN - 1) inputsRef.current[i + 1]?.focus(); },
    Enter: () => submit(),
  };
  const fn = actions[e.key];
  if (fn) fn();
};

const buildPasteHandler = ({ setDigits, inputsRef, submitWith }) => (e) => {
  const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, CODE_LEN);
  if (!text) return;
  e.preventDefault();
  const next = Array(CODE_LEN).fill('').map((_, i) => text[i] || '');
  setDigits(next);
  const lastFilled = Math.min(text.length, CODE_LEN) - 1;
  inputsRef.current[Math.min(lastFilled + 1, CODE_LEN - 1)]?.focus();
  if (text.length === CODE_LEN) setTimeout(() => submitWith(text), 50);
};

const VerifyPhonePage = () => {
  const nav = useNavigate();
  const location = useLocation();
  const { t } = useCMS();
  const { setCustomerSession } = useAuth();
  const { email, phone, redirectTo } = location.state || {};

  const [digits, setDigits] = useState(Array(CODE_LEN).fill(''));
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputsRef = useRef([]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // Start with a cooldown so they can't spam right after register sent the first code
  useEffect(() => { if (email) setCooldown(RESEND_COOLDOWN); }, [email]);

  // Auto-focus the first empty box on mount
  useEffect(() => { if (email) inputsRef.current[0]?.focus(); }, [email]);

  // Bounce away if the page was visited directly (no registration in progress)
  if (!email) return <Navigate to="/account" replace />;

  const setDigit = (i, val) => {
    const v = (val || '').replace(/\D/g, '').slice(0, 1);
    setDigits((prev) => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
    if (v && i < CODE_LEN - 1) inputsRef.current[i + 1]?.focus();
  };

  const submitWith = async (code) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post('/customers/verify-phone', { email, code });
      setCustomerSession(data.customer);
      toast.success('Phone verified — welcome!');
      nav(redirectTo || '/', { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Verification failed';
      toast.error(msg);
      setDigits(Array(CODE_LEN).fill(''));
      inputsRef.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e?.preventDefault?.();
    const code = digits.join('');
    if (code.length !== CODE_LEN) {
      toast.error('Enter all 6 digits');
      return;
    }
    submitWith(code);
  };

  const onKeyDown = buildKeyDownHandler({ digits, inputsRef, setDigit, submit: () => submit() });
  const onPaste = buildPasteHandler({ setDigits, inputsRef, submitWith });

  const resend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    try {
      await api.post('/customers/resend-code', { email });
      toast.success('New code sent');
      setCooldown(RESEND_COOLDOWN);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resend code');
    } finally {
      setResending(false);
    }
  };

  const masked = phone ? phone.replace(/^(\+?\d{2,3})\d+(\d{3})$/, '$1•••••$2') : '';

  return (
    <SignInBackground>
      <div className="w-full max-w-md">
        <button
          onClick={() => nav('/account')}
          className="mb-4 text-sm text-white/55 hover:text-white inline-flex items-center gap-1"
          data-testid="verify-back-btn"
        >
          <ChevronLeft size={16} /> Back
        </button>

        <div className="flex flex-col items-center mb-6">
          <BrandLogo name={t('brand.name', 'FAT BUSTERS')} variant="dark" />
          <p className="mt-3 text-xs uppercase tracking-[0.4em] text-red-400/90">Verify your phone</p>
        </div>

        <div className="fb-signin-card rounded-2xl overflow-hidden p-7">
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-4">
              <Smartphone size={26} className="text-red-400" />
            </div>
            <h1 className="text-white font-bold text-xl">Enter the 6-digit code</h1>
            <p className="mt-2 text-sm text-white/55">
              We sent a code to <b className="text-white">{masked || 'your phone'}</b>.
            </p>
          </div>

          <form onSubmit={submit} className="mt-6">
            <div className="flex justify-between gap-2" onPaste={onPaste}>
              {digits.map((d, i) => (
                <input
                  key={DIGIT_SLOT_IDS[i]}
                  ref={(el) => (inputsRef.current[i] = el)}
                  value={d}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete={i === 0 ? 'one-time-code' : 'off'}
                  maxLength={1}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onKeyDown={(e) => onKeyDown(i, e)}
                  className="w-12 h-14 text-center text-2xl font-bold text-white bg-black/40 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-400/50 transition-colors"
                  data-testid={`verify-digit-${i}`}
                />
              ))}
            </div>

            <button
              type="submit"
              disabled={busy || digits.some((d) => !d)}
              data-testid="verify-submit-btn"
              className="w-full mt-6 py-3 bg-red-500 hover:bg-red-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg flex items-center justify-center gap-2 shadow-lg shadow-red-500/30 transition-colors"
            >
              {busy ? <><Loader2 size={16} className="animate-spin" /> Verifying…</> : 'Verify & continue'}
            </button>
          </form>

          <div className="mt-5 text-center text-sm">
            <button
              type="button"
              onClick={resend}
              disabled={cooldown > 0 || resending}
              data-testid="verify-resend-btn"
              className="inline-flex items-center gap-1.5 text-red-400 hover:text-red-300 disabled:text-white/30 disabled:cursor-not-allowed font-semibold"
            >
              <RefreshCw size={13} className={resending ? 'animate-spin' : ''} />
              {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
            </button>
          </div>
        </div>

        <p className="text-center mt-5 text-xs text-white/40">
          Didn't get the code? Make sure your phone has reception and try again.
        </p>
      </div>
    </SignInBackground>
  );
};

export default VerifyPhonePage;
